import { Component, OnInit, HostListener, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router, ActivatedRoute } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatDatepickerIntl, MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { Auth } from '@aws-amplify/auth';
import { Storage } from '@aws-amplify/storage';
import { AuthService } from '../auth/auth.service';
import { CommonUtil } from '../util/common-util';
import { ToastUtil } from '../util/toast-util';
import { Constant } from '../constant';
import { AttachmentItem, Approver } from '../model/my-event';
import { UnionConsultationForm, ConsultationSession } from '../model/union-consultation';
import { GeneralYesNoDialogComponent } from '../dialog/general-yes-no-dialog/general-yes-no-dialog.component';
import { UcMemberSelectDialogComponent } from '../dialog/uc-member-select-dialog/uc-member-select-dialog.component';
import { ManagerEditDialogComponent } from '../dialog/manager-edit-dialog/manager-edit-dialog.component';
import { CustomDatepickerIntl } from '../util/datepicker-intl-util';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { MatIconModule } from '@angular/material/icon';

export const UC_DATE_FORMATS = {
  parse: { dateInput: 'YYYY/MM/DD' },
  display: {
    dateInput: 'YYYY/MM/DD',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'YYYY/MM/DD',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

@Component({
  selector: 'app-union-consultation-edit',
  templateUrl: './union-consultation-edit.component.html',
  styleUrl: './union-consultation-edit.component.css',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatSelectModule,
    MatRadioModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatDialogModule,
    MatIconModule,
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'ja-JP' },
    { provide: MAT_DATE_FORMATS, useValue: UC_DATE_FORMATS },
    { provide: MatDatepickerIntl, useClass: CustomDatepickerIntl },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    }
  ],
})
export class UnionConsultationEditComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  readonly Constant = Constant;
  showSpinner = true;
  isSaving = false;
  isDeleting = false;
  isNew = false;
  consultationId: string | null = null;
  creatorUserId: string | null = null;

  // フォーム選択肢（event_master から取得）
  ucCategoryOptions:             { value: number; label: string }[] = [];
  readonly ucShareScopeRoleOptions: { value: number; label: string }[] = [{ value: 0, label: '管理以上のみ' }];
  ucRequestOptions:              { value: number; label: string }[] = [];
  ucReceiptMethodOptions:        { value: number; label: string }[] = [];
  ucUrgencyOptions:              { value: number; label: string }[] = [];
  ucAgeGroupOptions:             { value: number; label: string }[] = [];
  ucGenderOptions:               { value: number; label: string }[] = [];
  ucStatusOptions:               { value: number; label: string }[] = [];
  ucFinalResponseMethodOptions:  { value: number; label: string }[] = [];
  ucResultTypeOptions:           { value: number; label: string }[] = [];

  // 完了ステータスの event_master ID（isCompleted 判定用）
  private completedStatusId: number | null = null;

  // 完了時のデフォルト値（最終対応方法・結果）の event_master ID
  private defaultFinalResponseMethodId: number | null = null;
  private defaultResultTypeId: number | null = null;

  // スピナー制御（複数の並列ロードを追跡）
  private loadingCount = 0;

  private startLoading(): void {
    this.loadingCount++;
  }

  private endLoading(): void {
    if (--this.loadingCount <= 0) {
      this.loadingCount = 0;
      this.showSpinner = false;
    }
  }

  public isPermissionOpen = false;

  public authDescription;

  // ログインユーザー情報
  private myUserId: string | null = null;
  myUserInfo: Approver | null = null;

  // フォームデータ
  form: UnionConsultationForm = this.createEmptyForm();

  // 共有範囲（APIには登録しない）
  shareScopeRole = 0;
  shareMembers: Approver[] = [];

  // 起票者（新規: myUserInfo, 編集: APIから取得）
  drafterInfo: Approver | null = null;

  // 日付（Dateオブジェクト）
  sessionConsultDates: (Date | null)[] = [null];
  sessionResponseDates: (Date | null)[] = [null];

  // 相談者の所属（ManagerEditDialog用）
  consultantAffiliation: any[] = [];

  // 添付ファイル
  attachments: AttachmentItem[] = [];
  isUploading = false;

  private readonly allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'ppt', 'pptx', 'xls', 'xlsx', 'doc', 'docx'];

  constructor(
    private title: Title,
    private router: Router,
    private route: ActivatedRoute,
    private commonUtil: CommonUtil,
    private toastUtil: ToastUtil,
    private auth: AuthService,
    private dialog: MatDialog,
    private http: HttpClient,
  ) {
    this.title.setTitle(Constant.pageTitleUnionConsultation + Constant.pageTitleCommon);
  }

  @HostListener('window:focus')
  onFocus(): void {
    this.commonUtil.checkRefreshToken(this.auth);
  }

  ngOnInit(): void {
    window.scroll(0, 0);
    this.loadFormOptions();
    const id = this.route.snapshot.paramMap.get('id');
    if (id === 'new') {
      this.isNew = true;
    } else {
      this.consultationId = id;
      this.isNew = false;
      this.loadData();
    }
    // 相談対応追加時のデフォルト主な対応者（編集者）として、新規・編集どちらでも自分の情報を取得する
    this.loadMyUserInfo();
    this.loadAuthDescription();
  }

  togglePermission() {
    this.isPermissionOpen = !this.isPermissionOpen;
  }

  private loadAuthDescription() {

    Auth.currentSession().then(session => {
      const options = this.auth.createApiHeader(session);

      this.commonUtil
        .apiGet('/master/authdescription', options)
        .then(res => {
          if (res.status === Constant.OK) {

            this.authDescription = res.data.auth_description
          }
        })
        .catch(err => {
          this.commonUtil.debug().log(err);
        });
    });
  }

  // ===== Getters =====

  get canSave(): boolean {
    const f = this.form;
    const hasCategory = f.categories.some(c => c !== null);
    const baseValid = !!f.title.trim() && hasCategory && !!f.summary.trim() &&
      !!f.receiptMethod && !!f.urgency && !!f.consultant.name.trim() &&
      !!f.consultant.contact.trim() && !!f.status;
    if (this.isCompleted) {
      return baseValid && !!f.finalResponseMethod;
    }
    return baseValid;
  }

  get isCompleted(): boolean {
    return this.completedStatusId !== null && this.form.status === this.completedStatusId;
  }

  get canAddSession(): boolean {
    return this.form.sessions.length < Constant.ucMaxSessions;
  }

  get canAddAttachment(): boolean {
    return this.attachments.length < Constant.ucMaxAttachments;
  }

  // ===== ステータス操作 =====

  onStatusChange(): void {
    if (!this.isCompleted) return;
    if (this.form.finalResponseMethod === null) {
      this.form.finalResponseMethod = this.defaultFinalResponseMethodId;
    }
    if (this.form.resultType === null) {
      this.form.resultType = this.defaultResultTypeId;
    }
  }

  // ===== 日付フォーマット =====

  formatDate(index: number, type: 'consult' | 'response'): string {
    const d = type === 'consult'
      ? this.sessionConsultDates[index]
      : this.sessionResponseDates[index];
    return d ? moment(d).format('YYYY/MM/DD') : '';
  }

  // ===== カテゴリー操作 =====

  isCategorySelected(value: number): boolean {
    return this.form.categories.includes(value);
  }

  getCategoryPriority(value: number): number | null {
    const idx = this.form.categories.indexOf(value);
    return idx >= 0 ? idx + 1 : null;
  }

  isCategoryDisabled(value: number): boolean {
    const full = this.form.categories.filter(c => c !== null).length >= 3;
    return full && !this.isCategorySelected(value);
  }

  toggleCategory(value: number): void {
    const idx = this.form.categories.indexOf(value);
    // 選択済みの場合は解除し、後続の優先順位を繰り上げる
    if (idx >= 0) {
      this.form.categories.splice(idx, 1);
      return;
    }
    if (this.form.categories.filter(c => c !== null).length < 3) {
      const empty = this.form.categories.indexOf(null);
      if (empty >= 0) {
        this.form.categories[empty] = value;
      } else {
        this.form.categories.push(value);
      }
    }
  }

  resetCategories(): void {
    this.form.categories = [];
  }

  // ===== 共有範囲 =====

  onAddShareMember(): void {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    if (!this.commonUtil.isOnline()) {
      this.toastUtil.showErrorToast('', Constant.msgNetworkError, Constant.toastShowMiliSec);
      return;
    }

    const excludedIds = [
      this.drafterInfo?.id,
      ...this.shareMembers.map(m => m.id)
    ].filter(Boolean);

    const dialogRef = this.dialog.open(UcMemberSelectDialogComponent, {
      width: '700px',
      height: '92vh',
      maxHeight: '1200px',
      autoFocus: false,
      disableClose: true,
      data: {
        selectedMembers: this.shareMembers,
        excludedUserIds: excludedIds,
      }
    });

    dialogRef.afterClosed().subscribe((result: Approver[] | false) => {
      if (result !== false) {
        this.shareMembers = result as Approver[];
      }
    });
  }

  removeShareMember(index: number): void {
    this.shareMembers.splice(index, 1);
  }

  // ===== 相談者所属 =====

  onSelectAffiliation(): void {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    if (!this.commonUtil.isOnline()) {
      this.toastUtil.showErrorToast('', Constant.msgNetworkError, Constant.toastShowMiliSec);
      return;
    }

    const dialogRef = this.dialog.open(ManagerEditDialogComponent, {
      data: { selected: this.consultantAffiliation },
      width: Constant.managerEditDialogWidth,
      height: '664px',
      autoFocus: false,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== Constant.empty) {
        this.consultantAffiliation = result ?? [];
      }
    });
  }

  // ===== 主な対応者 =====

  onSelectResponder(sessionIndex: number): void {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    if (!this.commonUtil.isOnline()) {
      this.toastUtil.showErrorToast('', Constant.msgNetworkError, Constant.toastShowMiliSec);
      return;
    }

    const session = this.form.sessions[sessionIndex];
    const excludedIds = session.responders.map(r => r.id);

    const dialogRef = this.dialog.open(UcMemberSelectDialogComponent, {
      width: '700px',
      height: '92vh',
      maxHeight: '1200px',
      autoFocus: false,
      disableClose: true,
      data: {
        selectedMembers: session.responders,
        excludedUserIds: excludedIds,
      }
    });

    dialogRef.afterClosed().subscribe((result: Approver[] | false) => {
      if (result !== false) {
        this.form.sessions[sessionIndex].responders = result as Approver[];
      }
    });
  }

  removeResponder(sessionIndex: number, responderIndex: number): void {
    this.form.sessions[sessionIndex].responders.splice(responderIndex, 1);
  }

  // ===== セッション操作 =====

  addSession(): void {
    if (!this.canAddSession) return;
    this.form.sessions.push(this.createEmptySession());
    this.sessionConsultDates.push(null);
    this.sessionResponseDates.push(null);
  }

  removeSession(index: number): void {
    if (index === 0) return;
    const s = this.form.sessions[index];
    const hasInput = !!s.consultDate || !!s.detail || !!s.responseDate || !!s.responseResult || !!s.toConsultant || !!s.toRelated;
    if (hasInput) {
      const dialogRef = this.dialog.open(GeneralYesNoDialogComponent, {
        width: Constant.dialogWidth,
        autoFocus: false,
        data: { msg: Constant.msgNoticeDelConfirm }
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.form.sessions.splice(index, 1);
          this.sessionConsultDates.splice(index, 1);
          this.sessionResponseDates.splice(index, 1);
        }
      });
    } else {
      this.form.sessions.splice(index, 1);
      this.sessionConsultDates.splice(index, 1);
      this.sessionResponseDates.splice(index, 1);
    }
  }

  onConsultDateChange(index: number, value: Date | null): void {
    this.sessionConsultDates[index] = value;
    this.form.sessions[index].consultDate = value ? moment(value).format('YYYY-MM-DD') : null;
  }

  onResponseDateChange(index: number, value: Date | null): void {
    this.sessionResponseDates[index] = value;
    this.form.sessions[index].responseDate = value ? moment(value).format('YYYY-MM-DD') : null;
  }

  // ===== ファイル添付 =====

  onFileSelect(): void {
    this.fileInput.nativeElement.click();
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    input.value = '';

    if (this.attachments.length >= Constant.ucMaxAttachments) return;

    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!this.allowedExtensions.includes(ext)) {
      this.toastUtil.showErrorToast('', Constant.msgErrorInvalidFile, Constant.toastShowMiliSecErr);
      return;
    }

    if (file.size > Constant.ucAttachmentMaxSize) {
      this.toastUtil.showErrorToast('', Constant.msgErrorLargeFile, Constant.toastShowMiliSecErr);
      return;
    }

    this.isUploading = true;
    const storageConfig = environment.amplify.Storage['myeventAttachment'];
    const s3PutConfig = {
      bucket: storageConfig.bucket,
      region: storageConfig.region,
      level: storageConfig.level as 'private' | 'public' | 'protected',
      contentType: file.type,
    };

    Auth.currentUserCredentials()
      .then(credentials => {
        const fileKey = Date.now().toString() + '.' + ext;
        return Storage.put(fileKey, file, s3PutConfig).then(result => ({
          filePath: credentials.identityId + '/' + (result as any)['key'],
          fileKey: (result as any)['key'],
        }));
      })
      .then(({ filePath, fileKey }) => {
        this.attachments.push({ fileName: file.name, filePath, fileKey });
        this.isUploading = false;
      })
      .catch(() => {
        this.toastUtil.showErrorToast('', Constant.msgErrorUploadFile, Constant.toastShowMiliSecErr);
        this.isUploading = false;
      });
  }

  onDownloadAttachment(attachment: AttachmentItem): void {
    const storageConfig = environment.amplify.Storage['myeventAttachment'];
    const s3GetConfig = {
      bucket: storageConfig.bucket,
      region: storageConfig.region,
      level: storageConfig.level as 'private' | 'public' | 'protected',
      expires: 3600,
    };
    Storage.get(attachment.fileKey, s3GetConfig)
      .then((url: any) => {
        this.http.get(url, { responseType: 'blob' }).subscribe(blob => {
          saveAs(blob, attachment.fileName);
        });
      })
      .catch(() => {
        this.toastUtil.showErrorToast('', Constant.msgFileDownloadError, Constant.toastShowMiliSec);
      });
  }

  removeAttachment(index: number): void {
    this.attachments.splice(index, 1);
  }

  // ===== 保存 =====

  onSave(): void {
    if (!this.canSave || this.isSaving) return;
    if (!this.commonUtil.checkOnline()) return;

    this.isSaving = true;

    Auth.currentSession().then(session => {
      const options = this.auth.createApiHeader(session);
      const body = this.buildSaveBody();
      options['body'] = body;

      const request = this.commonUtil.apiPut(Constant.apiPathUnionConsultation, options);

      request
        .then(res => {
          if (this.isNew && res.data?.id) {
            this.consultationId = res.data.id;
            this.isNew = false;
            this.router.navigate(['/union-consultation', this.consultationId], { replaceUrl: true });
          }
          this.form.lastUpdatedAt = res.data?.lastUpdatedAt ?? this.form.lastUpdatedAt;
          this.form.lastUpdatedPosition = res.data?.lastUpdatedPosition ?? this.form.lastUpdatedPosition;
          this.form.lastUpdatedName = res.data?.lastUpdatedName ?? this.form.lastUpdatedName;
          this.toastUtil.showInformationToast('', Constant.msgResultSaveSuccess, Constant.toastShowMiliSec);
        })
        .catch(err => {
          this.commonUtil.debug().log(err);
          if (err.message === Constant.msgLoginFailed) {
            this.commonUtil.showReloadDialog(this.auth);
          } else {
            this.toastUtil.showErrorToast('', Constant.msgSaveFailedRetry, Constant.toastShowMiliSecErr);
          }
        })
        .finally(() => {
          this.isSaving = false;
        });
    });
  }

  // ===== 戻る =====

  onCancel(): void {
    const dialogRef = this.dialog.open(GeneralYesNoDialogComponent, {
      width: Constant.dialogWidth,
      autoFocus: false,
      data: { msg: Constant.msgConfirmBack }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.router.navigate(['/union-consultation']);
      }
    });
  }

  // ===== 削除 =====

  onDelete(): void {
    const dialogRef = this.dialog.open(GeneralYesNoDialogComponent, {
      width: Constant.dialogWidth,
      autoFocus: false,
      data: { msg: 'この相談を削除します。\nよろしいですか？' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;

      if (this.isNew) {
        this.toastUtil.showInformationToast('', Constant.msgResultDeleteSuccess, Constant.toastShowMiliSec);
        this.router.navigate(['/union-consultation']);
        return;
      }

      this.isDeleting = true;
      Auth.currentSession().then(session => {
        const options = this.auth.createApiHeader(session);
        this.commonUtil.apiDel(`${Constant.apiPathUnionConsultation}/${this.consultationId}`, options)
          .then(() => {
            this.toastUtil.showInformationToast('', Constant.msgResultDeleteSuccess, Constant.toastShowMiliSec);
            this.router.navigate(['/union-consultation']);
          })
          .catch(err => {
            this.commonUtil.debug().log(err);
            if (err.message === Constant.msgLoginFailed) {
              this.commonUtil.showReloadDialog(this.auth);
            } else {
              this.toastUtil.showErrorToast('', Constant.msgNetworkError, Constant.toastShowMiliSec);
            }
          })
          .finally(() => {
            this.isDeleting = false;
          });
      });
    });
  }

  // ===== ラベル取得 =====

  getCategoryLabel(value: number): string {
    return this.ucCategoryOptions.find(c => c.value === value)?.label ?? '';
  }

  // ===== データ読み込み =====

  private loadData(): void {
    if (!this.commonUtil.isOnline()) {
      this.toastUtil.showErrorToast('', Constant.msgNetworkError, Constant.toastShowMiliSec);
      return;
    }

    this.startLoading();
    Auth.currentSession().then(session => {
      const options = this.auth.createApiHeader(session);
      this.commonUtil.apiGet(`${Constant.apiPathUnionConsultation}/${this.consultationId}`, options)
        .then(res => {
          this.applyFormData(res.data);
          this.endLoading();
        })
        .catch(err => {
          this.commonUtil.debug().log(err);
          this.endLoading();
          if (err.message === Constant.msgLoginFailed) {
            this.commonUtil.showReloadDialog(this.auth);
          } else if (err.message === '相談が存在しません。' || err.message === '相談へのアクセス権限がありません。') {
            this.router.navigate(['/error']);
          } else {
            this.toastUtil.showErrorToast('', Constant.msgNetworkError, Constant.toastShowMiliSec);
          }
        });
    }).catch(() => {
      this.endLoading();
    });
  }

  private loadFormOptions(): void {
    this.startLoading();
    Auth.currentSession().then(session => {
      const apiOptions = this.auth.createApiHeader(session);
      this.commonUtil.apiGet('/myevent/master', apiOptions)
        .then(res => {
          const data = res.data ?? {};
          // ID を value として使う（attribute はデフォルト値の特定にのみ使用）
          const toOpts = (type: string) =>
            (data[type] ?? []).map((m: any) => ({ value: m.id as number, label: m.name as string }));
          const findId = (type: string, attr: string): number | null =>
            (data[type] ?? []).find((m: any) => m.attribute === attr)?.id ?? null;

          this.ucCategoryOptions            = (data.category_sub ?? [])
            .filter((m: any) => m.parentId === 4)
            .map((m: any) => ({ value: m.id as number, label: m.name as string }));
          this.ucRequestOptions             = toOpts('uc_request');
          this.ucReceiptMethodOptions       = toOpts('uc_receipt_method');
          this.ucUrgencyOptions             = toOpts('uc_urgency');
          this.ucAgeGroupOptions            = toOpts('uc_age_group');
          this.ucGenderOptions              = toOpts('uc_gender');
          this.ucStatusOptions              = toOpts('uc_status');
          this.ucFinalResponseMethodOptions = toOpts('uc_final_response_method');
          this.ucResultTypeOptions          = toOpts('uc_result_type');

          // 完了ステータス ID を保持
          this.completedStatusId = findId('uc_status', 'completed');

          // 完了時のデフォルト値（最終対応方法・結果）の ID を保持
          this.defaultFinalResponseMethodId = findId('uc_final_response_method', 'info_provide');
          this.defaultResultTypeId          = findId('uc_result_type',           'resolved');

          // 新規作成時のデフォルト値を ID でセット
          if (this.isNew) {
            this.form.request             = findId('uc_request',               'consult_method');
            this.form.receiptMethod       = findId('uc_receipt_method',        'offline_face');
            this.form.urgency             = findId('uc_urgency',               'very_high');
            this.form.consultant.ageGroup = findId('uc_age_group',             '10s');
            this.form.consultant.gender   = findId('uc_gender',                'male');
            this.form.status              = findId('uc_status',                'in_progress');
            this.form.finalResponseMethod = findId('uc_final_response_method', 'info_provide');
            this.form.resultType          = findId('uc_result_type',           'resolved');
          }

          this.endLoading();
        })
        .catch(() => {
          this.endLoading();
        });
    }).catch(() => {
      this.endLoading();
    });
  }

  private loadMyUserInfo(): void {
    Auth.currentSession().then(session => {
      const options = this.auth.createApiHeader(session);
      // account-about（個人設定）と同じ方法で自分の情報を取得
      Promise.all([
        this.commonUtil.apiGet('/master', options),
        this.commonUtil.apiGet('/myaccount', options),
      ]).then(([masterRes, accountRes]) => {
        if (!accountRes.data) return;

        const masters = masterRes.data?.master ?? {};
        const positions: any[] = masters[5] ?? [];
        const posId = String(accountRes.data.officialposition ?? '');
        const positionName = positions.find(p => String(p.id) === posId)?.name ?? '';

        // ユーザーIDはアクセストークンのペイロードから取得
        const userId: string = (session as any).getAccessToken().payload?.username ?? '';
        this.myUserId = userId;

        // organizations をパス文字列の配列に変換（approver-select-dialog と同じ方式）
        const typeMap: { [key: number]: string } = { 1: '本部', 2: '支部', 3: 'エリア' };
        const affiliations = [
          ...(accountRes.data.organizations ?? []).map((org: any) => {
            if (org.level === 0) return org.path as string;
            return `${typeMap[org.master_type] ?? ''} ${org.path}`.trim();
          }),
          ...(accountRes.data.concurrent_organizations ?? []).map((org: any) =>
            `専門部 ${org.path}`.trim()
          ),
        ];

        this.myUserInfo = {
          id: userId,
          name: `${accountRes.data.last_name ?? ''} ${accountRes.data.firstName ?? ''}`.trim(),
          position: positionName,
          affiliations,
        };

        if (this.isNew) {
          // 新規作成時は起票者 = 自分
          this.drafterInfo = this.myUserInfo;

          // 1件目の主な対応者に自分をセット
          if (this.form.sessions[0].responders.length === 0) {
            this.form.sessions[0].responders = [this.myUserInfo];
          }
        }
      }).catch(() => {});
    });
  }

  private applyFormData(data: any): void {
    if (!data) return;

    // 起票者（APIから取得）
    if (data.drafter) {
      this.drafterInfo = data.drafter as Approver;
    }

    this.form = {
      title: data.title ?? '',
      categories: data.categories ?? [],
      summary: data.summary ?? '',
      request: data.request ?? null,
      requestNote: data.requestNote ?? '',
      receiptMethod: data.receiptMethod ?? null,
      urgency: data.urgency ?? null,
      consultant: {
        name: data.consultant?.name ?? '',
        ageGroup: data.consultant?.ageGroup ?? null,
        gender: data.consultant?.gender ?? null,
        contact: data.consultant?.contact ?? '',
        affiliation: data.consultant?.affiliation ?? [],
      },
      relatedParties: data.relatedParties ?? '',
      reportLine: data.reportLine ?? '',
      sessions: (data.sessions?.length > 0 ? data.sessions : [this.createEmptySession()]).map((s: any) => ({
        consultDate: s.consultDate ?? null,
        detail: s.detail ?? '',
        responseDate: s.responseDate ?? null,
        responders: s.responders ?? [],
        responseResult: s.responseResult ?? '',
        toConsultant: s.toConsultant ?? '',
        toRelated: s.toRelated ?? '',
      })),
      status: data.status ?? null,
      finalResponseMethod: data.finalResponseMethod ?? null,
      resultType: data.resultType ?? null,
      attachments: data.attachments ?? [],
      memo: data.memo ?? '',
      lastUpdatedAt: data.lastUpdatedAt ?? '',
      lastUpdatedPosition: data.lastUpdatedPosition ?? '',
      lastUpdatedName: data.lastUpdatedName ?? '',
    };

    // 日付オブジェクトを復元
    this.sessionConsultDates = this.form.sessions.map(s =>
      s.consultDate ? moment(s.consultDate).toDate() : null
    );
    this.sessionResponseDates = this.form.sessions.map(s =>
      s.responseDate ? moment(s.responseDate).toDate() : null
    );
    this.consultantAffiliation = data.consultant?.affiliation ?? [];
    this.attachments = data.attachments ?? [];

    // 個別追加メンバーを復元
    this.shareMembers = data.additionalMembers ?? [];

    // myUserId のセット
    this.myUserId = data.myUserId ?? null;
  }

  private buildSaveBody(): any {
    return {
      ...(this.consultationId ? { id: this.consultationId } : {}),
      additionalMembers: this.shareMembers.map(m => m.id),
      title: this.form.title,
      categories: this.form.categories.filter(c => c !== null),
      summary: this.form.summary,
      request: this.form.request,
      requestNote: this.form.requestNote,
      receiptMethod: this.form.receiptMethod,
      urgency: this.form.urgency,
      consultant: {
        ...this.form.consultant,
        affiliation: this.consultantAffiliation.map((a: any) => a.id).filter((id: any) => id != null),
      },
      relatedParties: this.form.relatedParties,
      reportLine: this.form.reportLine,
      sessions: this.form.sessions,
      status: this.form.status,
      finalResponseMethod: this.isCompleted ? this.form.finalResponseMethod : null,
      resultType: this.isCompleted ? this.form.resultType : null,
      attachments: this.attachments,
      memo: this.form.memo,
    };
  }

  private createEmptyForm(): UnionConsultationForm {
    return {
      title: '',
      categories: [],
      summary: '',
      request: null,
      requestNote: '',
      receiptMethod: null,
      urgency: null,
      consultant: {
        name: '',
        ageGroup: null,
        gender: null,
        contact: '',
        affiliation: [],
      },
      relatedParties: '',
      reportLine: '',
      sessions: [this.createEmptySession()],
      status: null,
      finalResponseMethod: null,
      resultType: null,
      attachments: [],
      memo: '',
      lastUpdatedAt: '',
      lastUpdatedPosition: '',
      lastUpdatedName: '',
    };
  }

  private createEmptySession(): ConsultationSession {
    return {
      consultDate: null,
      detail: '',
      responseDate: null,
      responders: this.myUserInfo ? [this.myUserInfo] : [],
      responseResult: '',
      toConsultant: '',
      toRelated: '',
    };
  }
}
